/**
 * JiraRenderer — structured rendering for Jira issues in the detail panel.
 * Handles both single issue view and multi-issue list view.
 */

import { User, Calendar, Tag, ArrowUpCircle, Flag, MessageSquare } from 'lucide-react'
import { MessageContent } from '../MessageContent'
import { SupplementSections } from './SupplementSections'

// ---------------------------------------------------------------------------
// Single Issue
// ---------------------------------------------------------------------------

interface JiraIssue {
  key?: string
  summary?: string
  description?: string
  status?: { name?: string; statusCategory?: { colorName?: string } } | string
  priority?: { name?: string; iconUrl?: string } | string
  assignee?: { displayName?: string; emailAddress?: string } | string | null
  reporter?: { displayName?: string; emailAddress?: string } | string | null
  issuetype?: { name?: string; iconUrl?: string } | string
  created?: string
  updated?: string
  labels?: string[]
  components?: Array<{ name?: string }> | string[]
  fixVersions?: Array<{ name?: string }> | string[]
  resolution?: { name?: string } | string | null
  comment?: { comments?: Array<{ author?: { displayName?: string }; body?: string; created?: string }> }
  subtasks?: Array<{ key?: string; fields?: { summary?: string; status?: { name?: string } } }>
  /** Merged from supplement tool calls (e.g. get_issue_comments) */
  _comments?: Array<{ id?: string; author?: string; body?: string; created?: string; updated?: string }>
  _transitions?: Array<{ id?: string; name?: string }>
  [k: string]: unknown
}

function getStatusColor(status: string): string {
  const s = status.toLowerCase()
  if (s.includes('done') || s.includes('closed') || s.includes('resolved')) return 'bg-success-soft text-success-foreground border-success/30'
  if (s.includes('progress') || s.includes('review')) return 'bg-info-soft text-info-foreground border-info/30'
  if (s.includes('todo') || s.includes('open') || s.includes('new')) return 'bg-foreground/[0.06] text-foreground border-subtle'
  if (s.includes('block')) return 'bg-destructive-soft text-destructive-foreground border-destructive/30'
  return 'bg-accent-soft text-accent-soft-foreground border-accent/30'
}

function getPriorityColor(priority: string): string {
  const p = priority.toLowerCase()
  if (p.includes('critical') || p.includes('blocker') || p.includes('highest')) return 'text-destructive-foreground'
  if (p.includes('high')) return 'text-warning'
  if (p.includes('medium')) return 'text-warning'
  if (p.includes('low') || p.includes('minor')) return 'text-info'
  return 'text-subtle-foreground'
}

function str(val: unknown): string {
  if (!val) return ''
  if (typeof val === 'string') return val
  if (typeof val === 'number') return String(val)
  if (typeof val === 'object' && val !== null) {
    const obj = val as Record<string, unknown>
    // Try common Jira field shapes in priority order
    if (typeof obj.displayName === 'string') return obj.displayName
    if (typeof obj.name === 'string') return obj.name
    if (typeof obj.value === 'string') return obj.value
    if (typeof obj.key === 'string') return obj.key
    if (typeof obj.emailAddress === 'string') return obj.emailAddress
  }
  return ''
}


export function JiraIssueRenderer({ data }: { data: Record<string, unknown> }) {
  const issue = data as unknown as JiraIssue
  const status = str(issue.status)
  const priority = str(issue.priority)
  const assignee = str(issue.assignee)
  const reporter = str(issue.reporter)
  const issueType = str(issue.issuetype)
  const resolution = str(issue.resolution)

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-mono text-accent font-semibold">{issue.key}</span>
          {issueType && (
            <span className="text-xs text-muted-foreground bg-foreground/[0.06] rounded px-1.5 py-0.5">{issueType}</span>
          )}
        </div>
        <h3 className="text-sm font-semibold text-foreground leading-snug">{issue.summary}</h3>
      </div>

      {/* Status & Priority Row */}
      <div className="flex flex-wrap items-center gap-2">
        {status && (
          <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold ${getStatusColor(status)}`}>
            {status}
          </span>
        )}
        {priority && (
          <span className={`inline-flex items-center gap-1 text-xs font-medium ${getPriorityColor(priority)}`}>
            <Flag className="w-3 h-3" />
            {priority}
          </span>
        )}
        {resolution && (
          <span className="text-xs text-muted-foreground bg-foreground/[0.04] rounded px-2 py-0.5 border border-subtle">
            Resolution: {resolution}
          </span>
        )}
      </div>

      {/* Metadata Grid */}
      <div className="grid grid-cols-2 gap-3">
        {assignee && (
          <div className="flex items-center gap-2 text-xs">
            <User className="w-3.5 h-3.5 text-subtle-foreground shrink-0" />
            <div>
              <div className="text-[10px] uppercase tracking-wide text-subtle-foreground font-semibold">Assignee</div>
              <div className="text-foreground">{assignee}</div>
            </div>
          </div>
        )}
        {reporter && (
          <div className="flex items-center gap-2 text-xs">
            <User className="w-3.5 h-3.5 text-subtle-foreground shrink-0" />
            <div>
              <div className="text-[10px] uppercase tracking-wide text-subtle-foreground font-semibold">Reporter</div>
              <div className="text-foreground">{reporter}</div>
            </div>
          </div>
        )}
        {issue.created && (
          <div className="flex items-center gap-2 text-xs">
            <Calendar className="w-3.5 h-3.5 text-subtle-foreground shrink-0" />
            <div>
              <div className="text-[10px] uppercase tracking-wide text-subtle-foreground font-semibold">Created</div>
              <div className="text-foreground">{new Date(issue.created).toLocaleDateString()}</div>
            </div>
          </div>
        )}
        {issue.updated && (
          <div className="flex items-center gap-2 text-xs">
            <Calendar className="w-3.5 h-3.5 text-subtle-foreground shrink-0" />
            <div>
              <div className="text-[10px] uppercase tracking-wide text-subtle-foreground font-semibold">Updated</div>
              <div className="text-foreground">{new Date(issue.updated).toLocaleDateString()}</div>
            </div>
          </div>
        )}
      </div>

      {/* Labels */}
      {issue.labels && issue.labels.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-subtle-foreground font-semibold mb-1.5 flex items-center gap-1">
            <Tag className="w-3 h-3" /> Labels
          </div>
          <div className="flex flex-wrap gap-1.5">
            {issue.labels.map((label) => (
              <span key={label} className="rounded-full bg-info-soft border border-info/30 px-2.5 py-0.5 text-xs text-info-foreground font-medium">
                {label}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Components */}
      {issue.components && issue.components.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-subtle-foreground font-semibold mb-1.5">Components</div>
          <div className="flex flex-wrap gap-1.5">
            {issue.components.map((c, i) => (
              <span key={i} className="rounded bg-foreground/[0.06] px-2 py-0.5 text-xs text-muted-foreground">
                {typeof c === 'string' ? c : c.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Description — distinct content surface (subtle tint differential)
       * so it reads as "user-authored body content" rather than blending
       * into the panel canvas. Pattern reused across renderers. */}
      {issue.description && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-subtle-foreground font-semibold mb-1.5">Description</div>
          <div className="rounded-lg bg-foreground/[0.03] border border-subtle p-3 text-xs text-foreground leading-relaxed max-h-80 overflow-y-auto">
            {typeof issue.description === 'string'
              ? <MessageContent content={issue.description} />
              : <pre className="rounded bg-code-soft text-foreground p-2 text-xs font-mono whitespace-pre-wrap overflow-auto">{JSON.stringify(issue.description, null, 2)}</pre>
            }
          </div>
        </div>
      )}

      {/* Subtasks */}
      {issue.subtasks && issue.subtasks.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-subtle-foreground font-semibold mb-1.5 flex items-center gap-1">
            <ArrowUpCircle className="w-3 h-3" /> Subtasks ({issue.subtasks.length})
          </div>
          <div className="space-y-1">
            {issue.subtasks.map((sub) => (
              <div key={sub.key} className="flex items-center justify-between text-xs bg-foreground/[0.03] border border-subtle rounded px-2.5 py-1.5">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-accent">{sub.key}</span>
                  <span className="text-foreground">{sub.fields?.summary}</span>
                </div>
                {sub.fields?.status?.name && (
                  <span className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${getStatusColor(sub.fields.status.name)}`}>
                    {sub.fields.status.name}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Inline comments from the original issue response */}
      {!issue._comments && issue.comment?.comments && issue.comment.comments.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-subtle-foreground font-semibold mb-1.5 flex items-center gap-1">
            <MessageSquare className="w-3 h-3" /> Comments ({issue.comment.comments.length})
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {issue.comment.comments.slice(-5).map((comment, i) => (
              <div key={i} className="bg-foreground/[0.03] border border-subtle rounded-lg p-2.5 text-xs">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-foreground">{comment.author?.displayName || 'Unknown'}</span>
                  {comment.created && (
                    <span className="text-subtle-foreground">{new Date(comment.created).toLocaleDateString()}</span>
                  )}
                </div>
                <div className="text-muted-foreground">
                  <MessageContent content={comment.body || ''} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Merged supplement sections (comments, transitions, etc.) */}
      <SupplementSections data={data} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Multi-Issue List
// ---------------------------------------------------------------------------

export function JiraIssuesRenderer({ data }: { data: unknown[] }) {
  const issues = data as JiraIssue[]

  return (
    <div className="space-y-2">
      {issues.map((issue, i) => {
        const status = str(issue.status)
        const priority = str(issue.priority)
        const assignee = str(issue.assignee)

        return (
          <div
            key={issue.key || i}
            className="rounded-lg border border-subtle bg-foreground/[0.03] p-3 hover:border-accent/30 transition"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-xs font-mono text-accent font-semibold shrink-0">{issue.key}</span>
                  {status && (
                    <span className={`rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${getStatusColor(status)}`}>
                      {status}
                    </span>
                  )}
                </div>
                <p className="text-xs text-foreground font-medium line-clamp-2">{issue.summary}</p>
              </div>
              {priority && (
                <span className={`text-xs shrink-0 ${getPriorityColor(priority)}`}>
                  <Flag className="w-3.5 h-3.5" />
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
              {assignee && (
                <span className="flex items-center gap-1">
                  <User className="w-3 h-3" /> {assignee}
                </span>
              )}
              {issue.updated && (
                <span>{new Date(issue.updated).toLocaleDateString()}</span>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
